import { useState } from "react";
import { T } from "../../tokens";
import Bokeh from "../Bokeh";

/* ── Hero banner ──────────────────────────────────────────────── */
export function MarketingHero({
  eyebrow,
  title,
  highlight,
  description,
  children,
  align = "center",
  minHeight = 540,
}) {
  return (
    <section
      style={{
        position: "relative",
        minHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        padding: "88px 24px 64px",
        background: "radial-gradient(ellipse at 60% 0%,#2A1F08 0%,#0D0B08 65%)",
        textAlign: align,
      }}
    >
      <Bokeh count={20} />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 820,
          width: "100%",
          margin: "0 auto",
        }}
      >
        {eyebrow && (
          <div
            style={{
              fontSize: 11,
              color: T.gold,
              letterSpacing: ".22em",
              textTransform: "uppercase",
              marginBottom: 16,
              animation: "fyzFadeUp .6s ease",
            }}
          >
            {eyebrow}
          </div>
        )}
        <h1
          style={{
            fontFamily: "Cormorant Garamond,serif",
            fontSize: "clamp(36px,5.4vw,64px)",
            fontWeight: 300,
            color: T.text,
            lineHeight: 1.08,
            marginBottom: 20,
            animation: "fyzFadeUp .7s ease",
          }}
        >
          {title}
          {highlight && (
            <>
              {" "}
              <em
                style={{
                  color: T.goldLt,
                  fontWeight: 600,
                  fontStyle: "italic",
                }}
              >
                {highlight}
              </em>
            </>
          )}
        </h1>
        {description && (
          <p
            style={{
              fontSize: 16,
              color: T.muted,
              lineHeight: 1.8,
              maxWidth: 620,
              margin: align === "center" ? "0 auto 28px" : "0 0 28px",
              animation: "fyzFadeUp .8s ease",
            }}
          >
            {description}
          </p>
        )}
        {children && (
          <div
            style={{
              animation: "fyzFadeUp .9s ease",
              display: align === "center" ? "flex" : "block",
              justifyContent: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {children}
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Eyebrow + title pair for in-page sections ──────────────── */
export function SectionTitle({ eyebrow, title, description, center = false }) {
  return (
    <header
      style={{
        marginBottom: 28,
        textAlign: center ? "center" : "left",
        maxWidth: center ? 720 : "none",
        marginLeft: center ? "auto" : undefined,
        marginRight: center ? "auto" : undefined,
      }}
    >
      {eyebrow && (
        <div
          style={{
            fontSize: 11,
            color: T.gold,
            letterSpacing: ".18em",
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          {eyebrow}
        </div>
      )}
      <h2
        style={{
          fontFamily: "Cormorant Garamond,serif",
          fontSize: "clamp(26px,3.4vw,40px)",
          fontWeight: 400,
          color: T.text,
          lineHeight: 1.15,
          marginBottom: description ? 12 : 0,
        }}
      >
        {title}
      </h2>
      {description && (
        <p
          style={{
            fontSize: 15,
            color: T.muted,
            lineHeight: 1.8,
            maxWidth: center ? 600 : 680,
            marginLeft: center ? "auto" : undefined,
            marginRight: center ? "auto" : undefined,
          }}
        >
          {description}
        </p>
      )}
    </header>
  );
}

/* ── Stat ball ────────────────────────────────────────────────── */
export function Stat({ value, label, icon }) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "22px 20px",
        background: T.dark3,
        border: `1px solid ${T.goldBdr}`,
        borderRadius: 14,
      }}
    >
      {icon && (
        <div style={{ fontSize: 22, marginBottom: 8, opacity: 0.8 }}>
          {icon}
        </div>
      )}
      <div
        style={{
          fontFamily: "Cormorant Garamond,serif",
          fontSize: 30,
          fontWeight: 600,
          color: T.goldLt,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 12,
          color: T.muted,
          marginTop: 6,
          letterSpacing: ".05em",
        }}
      >
        {label}
      </div>
    </div>
  );
}

/* ── Feature card ─────────────────────────────────────────────── */
export function FeatureCard({ icon, title, description }) {
  return (
    <article
      style={{
        background: T.dark3,
        border: `1px solid ${T.goldBdr}`,
        borderRadius: 14,
        padding: "24px 22px",
        transition: "all .25s",
        height: "100%",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.borderColor = T.gold;
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.borderColor = T.goldBdr;
        e.currentTarget.style.transform = "";
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: T.goldDim,
          border: `1px solid ${T.goldBdrS}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          marginBottom: 16,
        }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <h3
        style={{
          fontFamily: "Cormorant Garamond,serif",
          fontSize: 18,
          fontWeight: 600,
          color: T.text,
          marginBottom: 8,
        }}
      >
        {title}
      </h3>
      <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.75 }}>
        {description}
      </p>
    </article>
  );
}

/* ── Numbered step ────────────────────────────────────────────── */
export function StepCard({ step, icon, title, description }) {
  return (
    <article
      style={{
        background: T.dark3,
        border: `1px solid ${T.goldBdr}`,
        borderRadius: 14,
        padding: "24px 22px",
        position: "relative",
        height: "100%",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -14,
          left: 20,
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: `linear-gradient(135deg,${T.goldLt},${T.gold})`,
          color: T.dark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 700,
          fontFamily: "'DM Sans',sans-serif",
        }}
        aria-hidden="true"
      >
        {step}
      </div>
      {icon && (
        <div
          style={{
            fontSize: 24,
            marginTop: 4,
            marginBottom: 10,
          }}
          aria-hidden="true"
        >
          {icon}
        </div>
      )}
      <h3
        style={{
          fontFamily: "Cormorant Garamond,serif",
          fontSize: 18,
          fontWeight: 600,
          color: T.text,
          marginBottom: 8,
        }}
      >
        {title}
      </h3>
      <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.75 }}>
        {description}
      </p>
    </article>
  );
}

/* ── Testimonial ──────────────────────────────────────────────── */
export function Testimonial({ quote, name, role, avatar, rating = 5 }) {
  return (
    <figure
      style={{
        background: T.dark3,
        border: `1px solid ${T.goldBdr}`,
        borderRadius: 14,
        padding: "24px 22px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ color: T.gold, fontSize: 18 }} aria-hidden="true">
        {"★".repeat(rating)}
        {"☆".repeat(5 - rating)}
      </div>
      <blockquote
        style={{
          fontSize: 14.5,
          color: T.text,
          lineHeight: 1.8,
          fontStyle: "italic",
          flex: 1,
          borderLeft: `2px solid ${T.goldBdrS}`,
          paddingLeft: 12,
        }}
      >
        {quote}
      </blockquote>
      <figcaption style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: `linear-gradient(135deg,${T.goldDim},${T.dark4})`,
            border: `1px solid ${T.goldBdrS}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
          }}
          aria-hidden="true"
        >
          {avatar}
        </div>
        <div>
          <div style={{ fontSize: 13.5, color: T.text, fontWeight: 500 }}>
            {name}
          </div>
          <div style={{ fontSize: 11.5, color: T.muted }}>{role}</div>
        </div>
      </figcaption>
    </figure>
  );
}

/* ── Accordion item for FAQ ───────────────────────────────────── */
export function AccordionItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  const panelId = `faq-panel-${useId()}`;
  return (
    <div
      style={{
        background: T.dark3,
        border: `1px solid ${open ? T.goldBdrS : T.goldBdr}`,
        borderRadius: 12,
        marginBottom: 10,
        transition: "border-color .2s",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "16px 18px",
          background: "transparent",
          border: "none",
          color: T.text,
          fontSize: 15,
          fontFamily: "inherit",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <span style={{ fontWeight: 500 }}>{question}</span>
        <span
          aria-hidden="true"
          style={{
            fontSize: 20,
            color: T.gold,
            transform: open ? "rotate(45deg)" : "rotate(0)",
            transition: "transform .2s",
            lineHeight: 1,
          }}
        >
          +
        </span>
      </button>
      {open && (
        <div
          id={panelId}
          style={{
            padding: "0 18px 18px",
            fontSize: 13.5,
            color: T.muted,
            lineHeight: 1.8,
          }}
        >
          {answer}
        </div>
      )}
    </div>
  );
}

let _idCounter = 0;
function useId() {
  const [id] = useState(() => ++_idCounter);
  return id;
}

/* ── Callout banner ───────────────────────────────────────────── */
export function CalloutBanner({
  eyebrow,
  title,
  description,
  actions,
  emoji,
}) {
  return (
    <aside
      style={{
        background: "linear-gradient(120deg,#2A1C08,#1C1208,#2A1C08)",
        border: `1px solid ${T.goldBdrS}`,
        borderRadius: 20,
        padding: "40px 44px",
        position: "relative",
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: emoji ? "1fr auto" : "1fr",
        alignItems: "center",
        gap: 28,
      }}
    >
      <div
        style={{
          position: "absolute",
          right: -40,
          top: -40,
          width: 260,
          height: 260,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(201,168,76,.1) 0%,transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative" }}>
        {eyebrow && (
          <div
            style={{
              fontSize: 11,
              color: T.gold,
              letterSpacing: ".18em",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            {eyebrow}
          </div>
        )}
        <h3
          style={{
            fontFamily: "Cormorant Garamond,serif",
            fontSize: "clamp(22px,3vw,34px)",
            fontWeight: 400,
            color: T.text,
            lineHeight: 1.18,
            marginBottom: 10,
          }}
        >
          {title}
        </h3>
        {description && (
          <p
            style={{
              fontSize: 14.5,
              color: T.muted,
              lineHeight: 1.8,
              maxWidth: 520,
              marginBottom: actions ? 22 : 0,
            }}
          >
            {description}
          </p>
        )}
        {actions && (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {actions}
          </div>
        )}
      </div>
      {emoji && (
        <div
          aria-hidden="true"
          style={{ fontSize: 90, opacity: 0.35, lineHeight: 1 }}
        >
          {emoji}
        </div>
      )}
    </aside>
  );
}

/* ── Wrapper that applies the house content gutter ────────── */
export function Section({ background, children, padding = "64px 48px" }) {
  return (
    <section
      style={{
        background: background || "transparent",
        padding,
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>{children}</div>
    </section>
  );
}
